import { z } from "zod";

/**
 * Shared custody-wallet contract (architecture §8.3).
 * The api, web, custody-wallet, and bridge all use these shapes.
 * Agents add fields only through the project manager.
 */

export const CustodyApprovalAction = z.enum([
  "marketplace_list",
  "marketplace_cancel",
  "marketplace_buy",
  "shop_purchase",
  "burn",
  "buy_levels",
  "wager",
  "transfer",
  "withdraw",
]);
export type CustodyApprovalAction = z.infer<typeof CustodyApprovalAction>;

export const INTENT_VERSION = 1;

/** Amounts are decimal strings so canonical JSON does not depend on number formatting. */
export const CustodyIntent = z.discriminatedUnion("action", [
  z.object({ version: z.literal(INTENT_VERSION), action: z.literal("marketplace_list"), gameAccount: z.string(), connectedWallet: z.string(), params: z.object({ asset: z.string(), price: z.string() }) }),
  z.object({ version: z.literal(INTENT_VERSION), action: z.literal("marketplace_cancel"), gameAccount: z.string(), connectedWallet: z.string(), params: z.object({ listing: z.string() }) }),
  z.object({ version: z.literal(INTENT_VERSION), action: z.literal("marketplace_buy"), gameAccount: z.string(), connectedWallet: z.string(), params: z.object({ listing: z.string(), asset: z.string(), expectedPriceRcd: z.string() }) }),
  z.object({ version: z.literal(INTENT_VERSION), action: z.literal("buy_levels"), gameAccount: z.string(), connectedWallet: z.string(), params: z.object({ mon: z.string(), targetLevel: z.string(), maxRcd: z.string() }) }),
  z.object({ version: z.literal(INTENT_VERSION), action: z.literal("shop_purchase"), gameAccount: z.string(), connectedWallet: z.string(), params: z.object({ sku: z.string(), quantity: z.string(), rcdPaid: z.string(), itemMint: z.string(), amount: z.string() }) }),
  z.object({ version: z.literal(INTENT_VERSION), action: z.literal("transfer"), gameAccount: z.string(), connectedWallet: z.string(), params: z.object({ mintOrAsset: z.string(), amount: z.string(), destinationOwner: z.string() }) }),
  z.object({ version: z.literal(INTENT_VERSION), action: z.literal("withdraw"), gameAccount: z.string(), connectedWallet: z.string(), params: z.object({ assetOrMint: z.string(), amount: z.string(), destination: z.string() }) }),
  z.object({ version: z.literal(INTENT_VERSION), action: z.literal("burn"), gameAccount: z.string(), connectedWallet: z.string(), params: z.object({ asset: z.string() }) }),
  z.object({ version: z.literal(INTENT_VERSION), action: z.literal("wager"), gameAccount: z.string(), connectedWallet: z.string(), params: z.object({ stake: z.string(), opponent: z.string() }) }),
]);
export type CustodyIntent = z.infer<typeof CustodyIntent>;

/** Sorted-key JSON with no extra whitespace. This is the bytes that are hashed. */
export function canonicalIntentJson(intent: CustodyIntent): string {
  return JSON.stringify(sortKeys(intent));
}

/** Base58 of SHA-256 over the UTF-8 canonical JSON. */
export function custodyIntentHash(intent: CustodyIntent): string {
  return base58Encode(sha256(new TextEncoder().encode(canonicalIntentJson(intent))));
}

/** Readable Phantom `signMessage` text. Trailing newline is part of the signed bytes. */
export function custodyApprovalMessage(input: { summary: string; approvalId: string; expiresUtc: string; intentHash: string }): string {
  return `RedChain approval\n${input.summary}\nApproval id: ${input.approvalId}  Expires: ${input.expiresUtc}\nIntent: ${input.intentHash}\n`;
}

export const AllocateGameAccountResponse = z.object({
  playerIndex: z.number().int().nonnegative(),
  gameAccount: z.string(),
});
export type AllocateGameAccountResponse = z.infer<typeof AllocateGameAccountResponse>;

export const CustodyNonceRequest = z.object({
  connectedWallet: z.string().min(32),
  gameAccount: z.string().min(32),
});
export type CustodyNonceRequest = z.infer<typeof CustodyNonceRequest>;

/** Nonce custody-wallet issues before the connected wallet signs SIWS. */
export const CustodyNonceResponse = z.object({
  nonce: z.string().min(1),
  gameAccount: z.string(),
  /** How long the wallet has to sign this nonce. Not the session lifetime. */
  expiresAt: z.string(),
  /** SIWS expirationTime. issuedAt plus the session window, inside the custody-wallet check. */
  sessionExpiresAt: z.string().optional(),
});
export type CustodyNonceResponse = z.infer<typeof CustodyNonceResponse>;

export const CustodySessionProof = z.object({
  /** Full SIWS message, including the custody-wallet nonce, game account, and expiry. */
  message: z.string().min(1),
  /** Base58 signature from the connected wallet. */
  signature: z.string().min(1),
  connectedWallet: z.string(),
});
export type CustodySessionProof = z.infer<typeof CustodySessionProof>;

export const CustodyApproval = z.object({
  /** Exact text from `custodyApprovalMessage`, including the Intent line. */
  message: z.string().min(1),
  signature: z.string().min(1),
  approvalId: z.string().uuid(),
  connectedWallet: z.string(),
});
export type CustodyApproval = z.infer<typeof CustodyApproval>;

export const CustodySignRequest = z.object({
  gameAccount: z.string(),
  /** Base64 serialized transaction. */
  transaction: z.string().min(1),
  session: CustodySessionProof,
  approval: CustodyApproval.optional(),
});
export type CustodySignRequest = z.infer<typeof CustodySignRequest>;

export const CustodySignResponse = z.object({
  signature: z.string(),
});
export type CustodySignResponse = z.infer<typeof CustodySignResponse>;

export const CustodySignErrorCode = z.enum([
  "no_session",
  "forged_session",
  "expired_session",
  "missing_approval",
  "mismatched_approval",
  "reused_approval",
  "bad_destination",
  "bad_program",
  "malformed",
]);
export type CustodySignErrorCode = z.infer<typeof CustodySignErrorCode>;

export const CustodySignError = z.object({
  error: CustodySignErrorCode,
  detail: z.string().optional(),
});
export type CustodySignError = z.infer<typeof CustodySignError>;

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (value && typeof value === "object") {
    const src = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(src).sort()) out[key] = sortKeys(src[key]);
    return out;
  }
  return value;
}

function sha256(data: Uint8Array): Uint8Array {
  const k = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ]);
  const bitLen = data.length * 8;
  const withOne = data.length + 1;
  const pad = (64 - ((withOne + 8) % 64)) % 64;
  const buf = new Uint8Array(withOne + pad + 8);
  buf.set(data);
  buf[data.length] = 0x80;
  const view = new DataView(buf.buffer);
  view.setUint32(buf.length - 4, bitLen >>> 0);
  view.setUint32(buf.length - 8, Math.floor(bitLen / 0x100000000));
  let h0 = 0x6a09e667, h1 = 0xbb67ae85, h2 = 0x3c6ef372, h3 = 0xa54ff53a;
  let h4 = 0x510e527f, h5 = 0x9b05688c, h6 = 0x1f83d9ab, h7 = 0x5be0cd19;
  const w = new Uint32Array(64);
  for (let i = 0; i < buf.length; i += 64) {
    for (let t = 0; t < 16; t++) w[t] = view.getUint32(i + t * 4);
    for (let t = 16; t < 64; t++) {
      const s0 = rotr(w[t - 15]!, 7) ^ rotr(w[t - 15]!, 18) ^ (w[t - 15]! >>> 3);
      const s1 = rotr(w[t - 2]!, 17) ^ rotr(w[t - 2]!, 19) ^ (w[t - 2]! >>> 10);
      w[t] = (w[t - 16]! + s0 + w[t - 7]! + s1) >>> 0;
    }
    let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g = h6, h = h7;
    for (let t = 0; t < 64; t++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const temp1 = (h + S1 + ch + k[t]! + w[t]!) >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (S0 + maj) >>> 0;
      h = g; g = f; f = e; e = (d + temp1) >>> 0; d = c; c = b; b = a; a = (temp1 + temp2) >>> 0;
    }
    h0 = (h0 + a) >>> 0; h1 = (h1 + b) >>> 0; h2 = (h2 + c) >>> 0; h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0; h5 = (h5 + f) >>> 0; h6 = (h6 + g) >>> 0; h7 = (h7 + h) >>> 0;
  }
  const out = new Uint8Array(32);
  const ov = new DataView(out.buffer);
  [h0, h1, h2, h3, h4, h5, h6, h7].forEach((word, i) => ov.setUint32(i * 4, word));
  return out;
}

function rotr(x: number, n: number): number {
  return (x >>> n) | (x << (32 - n));
}

const B58 = "123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";

function base58Encode(bytes: Uint8Array): string {
  let zeros = 0;
  while (zeros < bytes.length && bytes[zeros] === 0) zeros++;
  const digits = [0];
  for (let i = zeros; i < bytes.length; i++) {
    let carry = bytes[i]!;
    for (let j = 0; j < digits.length; j++) {
      carry += digits[j]! << 8;
      digits[j] = carry % 58;
      carry = Math.floor(carry / 58);
    }
    while (carry > 0) {
      digits.push(carry % 58);
      carry = Math.floor(carry / 58);
    }
  }
  let out = "1".repeat(zeros);
  for (let i = digits.length - 1; i >= 0; i--) out += B58[digits[i]!]!;
  return out;
}
